import * as React from 'react';
import { useSelector } from 'react-redux';
import { PageTitle, selectUser } from 'gossamer-front-end';
import { MemberInviteSearch } from './MemberInviteSearch';
import { Alert, Tab, Tabs } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { UnverifiedMemberSearch } from './UnverifiedMemberSearch';
import { VerifiedMemberSearch } from './VerifiedMemberSearch';
import { AdminMemberSearch } from './AdminMemberSearch';
import { CustomInfo } from 'cb-unified';

export const useMembershipAccess = () => {
  const user = useSelector(selectUser);
  const isEnterprise =
    user?.roleId === 'staff' || (user?.customInfo as CustomInfo)?.adminInstitutionType === 'enterprise';
  const institutionType = (user.customInfo as CustomInfo | undefined)?.adminInstitutionType;
  const canInviteMembers = user.roleId === 'staff' || ['enterprise'].includes(institutionType || '');
  const canInviteAdmins = user.roleId === 'staff' || ['pro', 'premium', 'enterprise'].includes(institutionType || '');
  return { isEnterprise, canInviteMembers, canInviteAdmins };
};

export const MemberSearch = (): React.JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { canInviteMembers, isEnterprise, canInviteAdmins } = useMembershipAccess();
  return (
    <>
      <PageTitle
        title="Search Followers, Members and Admins"
        subtitle="Manage your institution's followers, members and admins"
      />

      <Tabs
        activeKey={searchParams.get('tab') || (isEnterprise ? 'verified' : 'unverified')}
        variant="underline"
        className="mb-3"
        mountOnEnter={true}
        unmountOnExit={true}
        onSelect={(tab) => {
          if (!tab) {
            return;
          }
          const newParams = new URLSearchParams();
          newParams.set('tab', tab);
          setSearchParams(newParams);
        }}
      >
        <Tab eventKey="unverified" title="Followers">
          <UnverifiedMemberSearch />
        </Tab>
        <Tab eventKey="verified" title="Members">
          <VerifiedMemberSearch />
        </Tab>
        {canInviteMembers && (
          <Tab eventKey="member-invites" title="Member Invites">
            <Alert variant="info">
              <Alert.Heading>This page has changed!</Alert.Heading>
              <p>
                You can now bulk-upload members from here using CSV (see button at the bottom of the page). This gives a
                more flexible format than the old upload.
              </p>
            </Alert>
            <MemberInviteSearch type="member" />
          </Tab>
        )}
        <Tab eventKey="admin" title="Admins">
          <AdminMemberSearch />
        </Tab>
        {canInviteAdmins && (
          <Tab eventKey="admin-invites" title="Admin Invites">
            <MemberInviteSearch type="admin" />
          </Tab>
        )}
      </Tabs>
    </>
  );
};
